// Markdown renderer for the Privacy Policy / Terms pages.
//
// Admin writes plain markdown in /admin/settings/legal; the public site
// renders it. We deliberately keep this small (no react-markdown) and add
// just the few extra blocks the privacy-policy layout needs:
//
// Inline / standard:
//   #, ##, ### headings   |  **bold** *italic* _italic_ `code`
//   [text](url) links     |  - / * bullet lists  |  1. numbered lists
//   blank-line paragraphs
//
// Custom blocks (used on Privacy Policy):
//   >>> text                    → lede paragraph (border-top, larger)
//   ::: info LABEL              → teal callout, LABEL in small caps
//     body...
//   :::
//   ::: warn LABEL              → amber callout
//     body...
//   :::
//   ::: defs                    → definition list
//     Term | definition text
//     Another term | its definition
//   :::
//   | col | col |               → standard markdown pipe-tables
//   |-----|-----|
//
// Template placeholders (resolved before rendering):
//   {{company_name}}, {{registered_office}}, {{cin}}, {{grievance_card}}
// — the renderer accepts a `vars` object and substitutes any matching key.
// {{grievance_card}} expands to <div class="grievance-card"></div> as a
// hook for the page to inject its own React component.

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// Inline formatters run after escaping so `<script>` in the source stays
// inert. Order matters: bold before italic so **a** doesn't trigger *a*.
const applyInline = (s) => s
  .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  .replace(/(?<!\*)\*(?!\s)([^*]+?)(?<!\s)\*(?!\*)/g, '<em>$1</em>')
  .replace(/_([^_]+)_/g, '<em>$1</em>')
  .replace(/`([^`]+)`/g, '<code>$1</code>')
  .replace(
    /\[([^\]]+)\]\((https?:[^\s)]+|#[A-Za-z0-9_-]+)\)/g,
    (_m, label, href) => href.startsWith('#')
      ? `<a href="${href}">${label}</a>`
      : `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`,
  );

// Substitute {{key}} placeholders before everything else. Unknown keys
// pass through untouched so the admin sees the literal placeholder
// instead of a silent blank.
// Page-level marker we can split the rendered HTML on so the React tree
// can swap in a real <GrievanceCard /> component instead of a static div.
export const GRIEVANCE_MARKER = '<!--GRIEVANCE_CARD-->';

const applyVars = (src, vars = {}) =>
  String(src).replace(/\{\{\s*([a-z_][a-z0-9_]*)\s*\}\}/gi, (full, key) => {
    if (key === 'grievance_card') return '\n\n@@GRIEVANCE_CARD@@\n\n';
    if (Object.prototype.hasOwnProperty.call(vars, key)) {
      const v = vars[key];
      return v == null ? '' : String(v);
    }
    return full;
  });

const renderHeading = (line) => {
  if (/^###\s+/.test(line)) return `<h3>${applyInline(escapeHtml(line.replace(/^###\s+/, '')))}</h3>`;
  if (/^##\s+/.test(line))  return `<h2>${applyInline(escapeHtml(line.replace(/^##\s+/, '')))}</h2>`;
  if (/^#\s+/.test(line))   return `<h1>${applyInline(escapeHtml(line.replace(/^#\s+/, '')))}</h1>`;
  return null;
};

const isBullet  = (line) => /^\s*[-*]\s+/.test(line);
const isOrdered = (line) => /^\s*\d+\.\s+/.test(line);
const isLede    = (line) => /^>>>\s+/.test(line);

// Pipe-table detection: `| col | col |` followed by a separator row of
// `|---|---|`. We only treat the block as a table if both rows are present.
const isTableHeader    = (line) => /^\s*\|.+\|\s*$/.test(line);
const isTableSeparator = (line) => /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/.test(line);

const splitRow = (line) => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());

const renderTable = (rows) => {
  if (rows.length < 2) return '';
  const head = rows[0];
  const body = rows.slice(2); // skip separator row
  const headHtml = `<thead><tr>${head.map((c) => `<th>${applyInline(escapeHtml(c))}</th>`).join('')}</tr></thead>`;
  const bodyHtml = `<tbody>${
    body.map((r) => `<tr>${r.map((c) => `<td>${applyInline(escapeHtml(c))}</td>`).join('')}</tr>`).join('')
  }</tbody>`;
  return `<table class="legal-table">${headHtml}${bodyHtml}</table>`;
};

const renderDefs = (lines) => {
  // Each non-empty line: "Term | definition" → <dt>Term</dt><dd>definition</dd>.
  const items = [];
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const idx = line.indexOf('|');
    if (idx === -1) {
      items.push(`<dt>${applyInline(escapeHtml(line))}</dt><dd></dd>`);
    } else {
      const term = line.slice(0, idx).trim();
      const def  = line.slice(idx + 1).trim();
      items.push(`<dt>${applyInline(escapeHtml(term))}</dt><dd>${applyInline(escapeHtml(def))}</dd>`);
    }
  }
  return `<dl class="legal-defs">${items.join('')}</dl>`;
};

const renderCallout = (kind, label, bodyLines) => {
  // Re-render the callout body through the main renderer so it can contain
  // paragraphs, links, etc. — but skip recursive callouts (no nesting).
  const body = renderBlocks(bodyLines, { _inCallout: true });
  const cls = kind === 'warn' ? 'legal-callout warn' : 'legal-callout info';
  const labelHtml = label
    ? `<span class="legal-callout-label">${applyInline(escapeHtml(label))}</span>`
    : '';
  return `<div class="${cls}">${labelHtml}${body}</div>`;
};

// Walk lines top-to-bottom, grouping consecutive list items into one
// <ul>/<ol>, table rows into one <table>, and folding everything else
// into paragraphs at blank-line boundaries.
const renderBlocks = (lines, _opts = {}) => {
  const out = [];
  let para = [];
  let listKind = null;       // 'ul' | 'ol' | null
  let listItems = [];

  const flushPara = () => {
    if (!para.length) return;
    const text = para.join(' ').trim();
    if (text) out.push(`<p>${applyInline(escapeHtml(text))}</p>`);
    para = [];
  };
  const flushList = () => {
    if (!listKind) return;
    out.push(`<${listKind}>${listItems.join('')}</${listKind}>`);
    listKind = null;
    listItems = [];
  };
  const flushAll = () => { flushPara(); flushList(); };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Blank line — closes paragraph + list.
    if (!line.trim()) {
      flushAll();
      i += 1;
      continue;
    }

    // Grievance card placeholder (post-substitution marker). We emit an
    // HTML comment so the page can split on it and inject a real React
    // component between the two halves.
    if (line.trim() === '@@GRIEVANCE_CARD@@') {
      flushAll();
      out.push(GRIEVANCE_MARKER);
      i += 1;
      continue;
    }

    // Lede paragraph: single-line, gets its own styled <p>.
    if (isLede(line)) {
      flushAll();
      out.push(`<p class="legal-lede">${applyInline(escapeHtml(line.replace(/^>>>\s+/, '')))}</p>`);
      i += 1;
      continue;
    }

    // Fenced custom blocks: ::: kind [LABEL...]
    const fenceOpen = /^:::\s*(\w+)\s*(.*)$/.exec(line);
    if (fenceOpen && line.trim() !== ':::') {
      flushAll();
      const kind = fenceOpen[1].toLowerCase();
      const label = fenceOpen[2].trim();
      const buf = [];
      i += 1;
      while (i < lines.length && lines[i].trim() !== ':::') {
        buf.push(lines[i]);
        i += 1;
      }
      i += 1; // consume closing :::
      if (kind === 'defs') {
        out.push(renderDefs(buf));
      } else if (kind === 'info' || kind === 'warn') {
        out.push(renderCallout(kind, label, buf));
      } else {
        // Unknown kind — render body as plain blocks so content isn't lost.
        out.push(renderBlocks(buf));
      }
      continue;
    }

    // Headings end any open paragraph/list and stand alone.
    const heading = renderHeading(line);
    if (heading) {
      flushAll();
      out.push(heading);
      i += 1;
      continue;
    }

    // Tables: header row + separator + data rows.
    if (isTableHeader(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      flushAll();
      const rows = [splitRow(line), splitRow(lines[i + 1])];
      i += 2;
      while (i < lines.length && isTableHeader(lines[i])) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      out.push(renderTable(rows));
      continue;
    }

    if (isBullet(line)) {
      flushPara();
      if (listKind && listKind !== 'ul') flushList();
      listKind = 'ul';
      listItems.push(`<li>${applyInline(escapeHtml(line.replace(/^\s*[-*]\s+/, '')))}</li>`);
      i += 1;
      continue;
    }

    if (isOrdered(line)) {
      flushPara();
      if (listKind && listKind !== 'ol') flushList();
      listKind = 'ol';
      listItems.push(`<li>${applyInline(escapeHtml(line.replace(/^\s*\d+\.\s+/, '')))}</li>`);
      i += 1;
      continue;
    }

    // Plain prose — append to the current paragraph buffer.
    flushList();
    para.push(line);
    i += 1;
  }

  flushAll();
  return out.join('');
};

export const renderLegalMarkdown = (src, vars = {}) => {
  if (!src) return '';
  const expanded = applyVars(src, vars);
  const lines = expanded.replace(/\r\n/g, '\n').split('\n');
  return renderBlocks(lines);
};

export default renderLegalMarkdown;
